<script setup lang="ts">
import { computed, ref } from "vue";
import { Trash2 } from "@lucide/vue";

import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import AddReferenceArea from "../ReferenceArea/AddReferenceArea.vue";
import { toBuildingTypeId } from "@/features/BuildingProject/utils/buildingTypeId";
import {
  grossArea,
  mainRowIndex,
  normalizeRow,
  projectTotalsFromRows,
  shares,
  type BuildingAreaRow,
} from "@/features/BuildingProject/utils/buildingAreaTable";
import format from "@/services/Utils/format";

type BuildingTypeOption = { name: string; value: number | string; disabled?: boolean };
type Side = "aboveGround" | "belowGround";
type RowFieldName =
  | "areaAboveGround"
  | "floorsAboveGround"
  | "heatedAreaAboveGround"
  | "areaBelowGround"
  | "floorsBelowGround"
  | "heatedAreaBelowGround";

const props = withDefaults(
  defineProps<{
    rows: BuildingAreaRow[];
    /** Every building type, group headings included as disabled options. */
    buildingTypeOptions: BuildingTypeOption[];
    /** The building types with a limit of their own under the calculation type; only these can share a building. */
    weightableBuildingTypeIds: number[];
    /** Whether rows can be added at all: only calculation types that weight a mixed-use limit. */
    canAddRows: boolean;
    wasValidated: boolean;
    isExistingProject?: boolean;
    /** Whether a row side's heated area is held to its gross area yet; stored data may already break it. */
    heatedAreaChecked?: (rowIndex: number, side: Side) => boolean;
  }>(),
  { isExistingProject: false, heatedAreaChecked: () => true }
);

const emit = defineEmits<{
  "update:rows": [rows: BuildingAreaRow[]];
}>();

const isMixed = computed(() => props.rows.length >= 2);
const mainIndex = computed(() => mainRowIndex(props.rows));
const rowShares = computed(() => shares(props.rows));
const totals = computed(() => projectTotalsFromRows(props.rows));
const totalGrossArea = computed(() => props.rows.reduce((sum, row) => sum + grossArea(row), 0));
const weightable = computed(() => new Set(props.weightableBuildingTypeIds));

const labelFor = (buildingTypeId: number | null): string =>
  props.buildingTypeOptions.find((option) => Number(option.value) === buildingTypeId)?.name.trim() ?? "Building type";

/** Which fields the reader has typed in, per row: a missing value is flagged once it's theirs, or on a save attempt. */
const touchedFields = ref(new Set<string>());
const touchedKey = (rowIndex: number, fieldName: RowFieldName) => `${rowIndex}:${fieldName}`;
const showsMissingInput = (rowIndex: number, fieldName: RowFieldName) =>
  props.wasValidated || touchedFields.value.has(touchedKey(rowIndex, fieldName));

// An existing single-use project keeps above ground unlocked, as it always has; every other row unlocks by its own area.
const aboveGroundUnlocked = (row: BuildingAreaRow) =>
  (props.isExistingProject && !isMixed.value) || (row.areaAboveGround ?? 0) > 0;
const belowGroundUnlocked = (row: BuildingAreaRow) => (row.areaBelowGround ?? 0) > 0;

/** Types a row may pick: all of them alone, else those with their own limit and not on another row. */
const optionsFor = (rowIndex: number): BuildingTypeOption[] => {
  if (!isMixed.value) return props.buildingTypeOptions;
  const current = props.rows[rowIndex].buildingTypeId;
  const taken = new Set(props.rows.filter((_, index) => index !== rowIndex).map((row) => row.buildingTypeId));
  const choosable = (option: BuildingTypeOption) =>
    Number(option.value) === current || (weightable.value.has(Number(option.value)) && !taken.has(Number(option.value)));
  // A group heading stays only while one of its own types is still offered beneath it.
  return props.buildingTypeOptions.filter((option, index, options) => {
    if (!option.disabled) return choosable(option);
    const nextHeading = options.findIndex((other, otherIndex) => otherIndex > index && other.disabled);
    return options.slice(index + 1, nextHeading < 0 ? undefined : nextHeading).some(choosable);
  });
};

const canAddRow = computed(
  () =>
    props.canAddRows &&
    props.rows.every((row) => row.buildingTypeId !== null && weightable.value.has(row.buildingTypeId)) &&
    props.buildingTypeOptions.some(
      (option) =>
        !option.disabled &&
        weightable.value.has(Number(option.value)) &&
        !props.rows.some((row) => row.buildingTypeId === Number(option.value))
    )
);

const writeRow = (rowIndex: number, change: Partial<BuildingAreaRow>) => {
  const next = { ...props.rows[rowIndex], ...change };
  // No basement on this row: its floors and heated area go with it, cleared rather than zeroed.
  if ("areaBelowGround" in change && !change.areaBelowGround) {
    next.floorsBelowGround = null;
    next.heatedAreaBelowGround = null;
  }
  // A new project's above ground at 0 has nothing to count, and the validator still asks for both.
  if ("areaAboveGround" in change && change.areaAboveGround === 0 && !props.isExistingProject) {
    next.floorsAboveGround = 0;
    next.heatedAreaAboveGround = 0;
  }
  emit(
    "update:rows",
    props.rows.map((row, index) => (index === rowIndex ? normalizeRow(next) : row))
  );
};

const typeInto = (rowIndex: number, fieldName: RowFieldName, value: number | null) => {
  touchedFields.value.add(touchedKey(rowIndex, fieldName));
  writeRow(rowIndex, { [fieldName]: value });
};

const selectType = (rowIndex: number, value: string) => writeRow(rowIndex, { buildingTypeId: toBuildingTypeId(value) });

const addRow = () => {
  emit("update:rows", [...props.rows, normalizeRow({ buildingTypeId: null, area: null })]);
};

const removeRow = (rowIndex: number) => {
  if (!isMixed.value) return;
  touchedFields.value = new Set();
  emit(
    "update:rows",
    props.rows.filter((_, index) => index !== rowIndex)
  );
};

const typeError = (row: BuildingAreaRow): string =>
  props.wasValidated && (row.buildingTypeId === null || row.buildingTypeId < 0) ? "Select a building type" : "";

const missing = (rowIndex: number, fieldName: RowFieldName, value: number | null | undefined, message: string) =>
  showsMissingInput(rowIndex, fieldName) && (value === null || value === undefined) ? message : undefined;

const areaAboveGroundError = (row: BuildingAreaRow, rowIndex: number): string | undefined => {
  if (isMixed.value && showsMissingInput(rowIndex, "areaAboveGround") && row.areaAboveGround === 0)
    return "Enter an area above 0";
  return missing(rowIndex, "areaAboveGround", row.areaAboveGround, "Enter an area");
};

const heatedExceeds = (row: BuildingAreaRow, rowIndex: number, side: Side): boolean => {
  if (!props.heatedAreaChecked(rowIndex, side)) return false;
  const [heated, gross] =
    side === "aboveGround" ? [row.heatedAreaAboveGround, row.areaAboveGround] : [row.heatedAreaBelowGround, row.areaBelowGround];
  return heated !== null && heated !== undefined && gross !== null && gross !== undefined && heated > gross;
};

const EXCEEDS_MESSAGE: Record<Side, string> = {
  aboveGround: "Heated area can't exceed the gross area above ground.",
  belowGround: "Heated area can't exceed the gross area below ground.",
};

const sideUnlocked = (row: BuildingAreaRow, side: Side) =>
  side === "aboveGround" ? aboveGroundUnlocked(row) : belowGroundUnlocked(row);

const exceedsTooltip = (row: BuildingAreaRow, rowIndex: number, side: Side): string | undefined =>
  sideUnlocked(row, side) && heatedExceeds(row, rowIndex, side) ? EXCEEDS_MESSAGE[side] : undefined;

const rowMessages = (row: BuildingAreaRow, rowIndex: number): string[] =>
  (["aboveGround", "belowGround"] as const).flatMap((side) => exceedsTooltip(row, rowIndex, side) ?? []);

const percent = (share: number): string => `${Math.round(share * 100)} %`;
const shareWidth = (share: number): string => `${Math.round(share * 100)}%`;
const formatTotal = (value: number | null): string => (value === null ? "" : format.number(value));

/** The main row's marker waits for some area, so a table still being filled in names no main type yet. */
const showsMainMarker = (rowIndex: number) => isMixed.value && rowIndex === mainIndex.value && totalGrossArea.value > 0;

/** The summary names the main type in bold beside the same marker as its row, so the colour points at that row. */
const summary = computed(() => {
  const main = props.rows[mainIndex.value];
  if (!showsMainMarker(mainIndex.value) || !main) return null;
  return {
    name: labelFor(main.buildingTypeId),
    rest: ` is the main type with ${format.number(grossArea(main))} of ${format.number(totalGrossArea.value)} m² gross area.`,
  };
});

const rowKey = (row: BuildingAreaRow, rowIndex: number) => `row-${row.buildingTypeId ?? `new-${rowIndex}`}`;

// The column minimums add up to the table's min width, which fits a 768 px screen; below it the table scrolls.
// The remove cell is pinned so a narrow card never hides it.
const GRID =
  "grid grid-cols-[minmax(10rem,1.6fr)_minmax(4.25rem,1fr)_3.5rem_minmax(4.25rem,1fr)_minmax(4.25rem,1fr)_3.5rem_minmax(4.25rem,1fr)_3.25rem_2rem] gap-x-2";
</script>

<template>
  <div
    class="flex flex-col gap-3"
    data-cy="building-area-table">
    <!-- Reaches 0.5rem into the card's padding, so the main row's tint has room without narrowing a column. -->
    <div class="-mx-2 overflow-x-auto">
      <div class="flex min-w-[44.5rem] flex-col gap-2 px-2">
        <div
          :class="GRID"
          aria-hidden="true">
          <span />
          <span class="t-caption text-type-muted col-span-3 border-b border-slate-300 pb-1 tracking-wide uppercase"
            >Above ground</span
          >
          <span class="t-caption text-type-muted col-span-3 border-b border-slate-300 pb-1 tracking-wide uppercase"
            >Below ground</span
          >
          <span class="t-caption text-type-muted tracking-wide uppercase">Share</span>
          <span />
        </div>
        <div
          :class="GRID"
          class="t-caption text-type-muted"
          aria-hidden="true">
          <span>Building type<span class="text-app-red-600">*</span></span>
          <span class="text-right">Area (m&#178;)<span class="text-app-red-600">*</span></span>
          <span class="text-right">Floors</span>
          <span class="text-right">Heated (m&#178;)</span>
          <span class="text-right">Area (m&#178;)</span>
          <span class="text-right">Floors</span>
          <span class="text-right">Heated (m&#178;)</span>
          <span>of total</span>
          <span />
        </div>

        <!-- Bleeds vertically only when mixed: a lone row is last, and its negative margin would scroll the wrapper. -->
        <div
          v-for="(row, rowIndex) in rows"
          :key="rowKey(row, rowIndex)"
          class="duration-fast ease-standard relative -mx-2 flex flex-col gap-1 px-2 transition-colors"
          :class="{ '-my-1 py-1': isMixed, 'bg-app-blue-50': showsMainMarker(rowIndex) }"
          data-cy="building-area-row">
          <Transition name="fade-in">
            <span
              v-if="showsMainMarker(rowIndex)"
              class="w-px-3 bg-app-blue-600 absolute inset-y-0 left-0"
              aria-hidden="true"
              data-cy="building-area-main-badge" />
          </Transition>
          <div
            :class="GRID"
            class="items-start">
            <div class="min-w-0">
              <SelectInputWithKeyValue
                :value="row.buildingTypeId"
                :options="optionsFor(rowIndex)"
                placeholder="Select building type"
                :aria-label="`Building type, row ${rowIndex + 1}`"
                :required="true"
                :error-msg="typeError(row)"
                @update:value="(value: string) => selectType(rowIndex, value)" />
              <span
                v-if="showsMainMarker(rowIndex)"
                class="sr-only"
                >(main type)</span
              >
            </div>
            <NumberInput
              :value="row.areaAboveGround"
              :aria-label="`${labelFor(row.buildingTypeId)} area above ground (m²)`"
              color="primary"
              :align-right="true"
              :positive="true"
              :required="true"
              :error-msg="areaAboveGroundError(row, rowIndex)"
              @update:value="(value: number | null) => typeInto(rowIndex, 'areaAboveGround', value)" />
            <NumberInput
              :key="`floors-above-${aboveGroundUnlocked(row)}`"
              :value="row.floorsAboveGround"
              :aria-label="`${labelFor(row.buildingTypeId)} floors above ground`"
              color="primary"
              :align-right="true"
              :positive="true"
              :no-decimals="true"
              :disabled="!aboveGroundUnlocked(row)"
              :required="aboveGroundUnlocked(row)"
              :error-msg="
                aboveGroundUnlocked(row)
                  ? missing(rowIndex, 'floorsAboveGround', row.floorsAboveGround, 'Enter floors')
                  : undefined
              "
              @update:value="(value: number | null) => typeInto(rowIndex, 'floorsAboveGround', value)" />
            <NumberInput
              :key="`heated-above-${aboveGroundUnlocked(row)}`"
              :value="row.heatedAreaAboveGround"
              :aria-label="`${labelFor(row.buildingTypeId)} heated area above ground (m²)`"
              color="primary"
              :align-right="true"
              :positive="true"
              :disabled="!aboveGroundUnlocked(row)"
              :required="aboveGroundUnlocked(row)"
              :error-msg="
                aboveGroundUnlocked(row)
                  ? missing(rowIndex, 'heatedAreaAboveGround', row.heatedAreaAboveGround, 'Enter heated area')
                  : undefined
              "
              :error-tooltip="exceedsTooltip(row, rowIndex, 'aboveGround')"
              data-cy="heated-floor-area-above-ground"
              @update:value="(value: number | null) => typeInto(rowIndex, 'heatedAreaAboveGround', value)" />
            <NumberInput
              :value="row.areaBelowGround"
              :aria-label="`${labelFor(row.buildingTypeId)} area below ground (m²)`"
              color="primary"
              :align-right="true"
              :positive="true"
              @update:value="(value: number | null) => typeInto(rowIndex, 'areaBelowGround', value)" />
            <NumberInput
              :key="`floors-below-${belowGroundUnlocked(row)}`"
              :value="row.floorsBelowGround"
              :aria-label="`${labelFor(row.buildingTypeId)} floors below ground`"
              color="primary"
              :align-right="true"
              :positive="true"
              :no-decimals="true"
              :disabled="!belowGroundUnlocked(row)"
              :required="belowGroundUnlocked(row)"
              :error-msg="
                belowGroundUnlocked(row)
                  ? missing(rowIndex, 'floorsBelowGround', row.floorsBelowGround, 'Enter floors')
                  : undefined
              "
              @update:value="(value: number | null) => typeInto(rowIndex, 'floorsBelowGround', value)" />
            <NumberInput
              :key="`heated-below-${belowGroundUnlocked(row)}`"
              :value="row.heatedAreaBelowGround"
              :aria-label="`${labelFor(row.buildingTypeId)} heated area below ground (m²)`"
              color="primary"
              :align-right="true"
              :positive="true"
              :disabled="!belowGroundUnlocked(row)"
              :required="belowGroundUnlocked(row)"
              :error-msg="
                belowGroundUnlocked(row)
                  ? missing(rowIndex, 'heatedAreaBelowGround', row.heatedAreaBelowGround, 'Enter heated area')
                  : undefined
              "
              :error-tooltip="exceedsTooltip(row, rowIndex, 'belowGround')"
              data-cy="heated-basement-area"
              @update:value="(value: number | null) => typeInto(rowIndex, 'heatedAreaBelowGround', value)" />
            <div
              class="flex h-9 flex-col justify-center gap-1"
              data-cy="building-area-share">
              <span class="t-small-bold text-type-default whitespace-nowrap tabular-nums">{{
                percent(rowShares[rowIndex])
              }}</span>
              <span class="rounded-4 block h-1 w-full bg-slate-200">
                <!-- style kept: the bar's width is the row's share, a value no utility class can express -->
                <span
                  class="rounded-4 bg-app-blue-600 block h-1"
                  :style="{ width: shareWidth(rowShares[rowIndex]) }" />
              </span>
            </div>
            <div
              class="duration-fast ease-standard sticky right-0 flex h-9 items-center justify-end border-l border-slate-200 transition-colors"
              :class="showsMainMarker(rowIndex) ? 'bg-app-blue-50' : 'bg-slate-0'">
              <button
                type="button"
                :aria-label="`Remove ${labelFor(row.buildingTypeId)}`"
                :aria-disabled="!isMixed || undefined"
                :title="isMixed ? 'Remove building type' : 'A building needs at least one building type'"
                data-cy="remove-building-type-btn"
                class="focus-ring duration-fast ease-standard rounded-4 flex h-7 w-7 items-center justify-center transition-colors"
                :class="isMixed ? 'interactive-active hover:bg-slate-100' : 'interactive-disabled'"
                @click="removeRow(rowIndex)">
                <Trash2
                  class="icon-sm"
                  :class="isMixed ? 'text-app-red-600' : 'text-slate-400'"
                  :stroke-width="1.5" />
              </button>
            </div>
          </div>
          <p
            v-for="message in rowMessages(row, rowIndex)"
            :key="message"
            class="t-caption text-app-red-700"
            role="alert">
            {{ message }}
          </p>
        </div>

        <div
          v-if="isMixed"
          :class="GRID"
          class="t-small-bold text-type-default border-t border-slate-300 pt-2 tabular-nums"
          data-cy="building-area-total">
          <span>Total</span>
          <span class="pr-3 text-right">{{ formatTotal(totals.areaAboveGround) }}</span>
          <span />
          <span class="pr-3 text-right">{{ formatTotal(totals.heatedAreaAboveGround) }}</span>
          <span class="pr-3 text-right">{{ formatTotal(totals.areaBelowGround) }}</span>
          <span />
          <span class="pr-3 text-right">{{ formatTotal(totals.heatedAreaBelowGround) }}</span>
          <span>100 %</span>
          <span />
        </div>
      </div>
    </div>

    <div
      v-if="canAddRows || summary"
      class="flex flex-wrap items-center justify-between gap-3">
      <AddReferenceArea
        v-if="canAddRow"
        label="Add building type"
        data-cy="add-building-type-btn"
        @add-reference-area="addRow" />
      <p
        v-else-if="canAddRows"
        class="t-caption text-type-muted">
        Pick a building type with its own limit value on every row to add another.
      </p>
      <p
        v-if="summary"
        class="t-caption text-type-muted ml-auto flex items-stretch gap-2"
        data-cy="building-area-summary">
        <span
          class="w-px-3 bg-app-blue-600 shrink-0"
          aria-hidden="true" />
        <span
          ><strong class="t-caption-bold text-type-default">{{ summary.name }}</strong
          >{{ summary.rest }}</span
        >
      </p>
    </div>
  </div>
</template>
